import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import { FLIP_WORDS } from '@/config/hero';
import { useLanguage } from '@/context/LanguageContext';
import { EditableContent } from '../EditableContent';

interface FlipTextProps {
    isRTL: boolean;
}

export const FlipText = ({ isRTL }: FlipTextProps) => {
    const { isFrozen } = useLanguage();
    const [currentIndex, setCurrentIndex] = useState(0);
    const words = isRTL ? FLIP_WORDS.ar : FLIP_WORDS.en;

    useEffect(() => {
        if (isFrozen) return;

        const interval = setInterval(() => {
            setCurrentIndex((prev) => (prev + 1) % words.length);
        }, 2500);
        return () => clearInterval(interval);
    }, [words.length, isFrozen]);

    return (
        <span className="relative inline-block min-w-[200px] sm:min-w-[280px]">
            <AnimatePresence mode="wait">
                <motion.span
                    key={currentIndex}
                    initial={{
                        rotateX: -90,
                        opacity: 0,
                        y: 20
                    }}
                    animate={{
                        rotateX: 0,
                        opacity: 1,
                        y: 0
                    }}
                    exit={{
                        rotateX: 90,
                        opacity: 0,
                        y: -20
                    }}
                    transition={{
                        duration: 0.5,
                        ease: [0.16, 1, 0.3, 1]
                    }}
                    className={cn(
                        "inline-block text-primary will-change-transform transform-gpu",
                        "sm:drop-shadow-[0_0_20px_hsl(var(--primary)/0.4)] drop-shadow-none"
                    )}
                    style={{
                        transformStyle: 'preserve-3d',
                        backfaceVisibility: 'hidden',
                        textShadow: '0 0 10px hsl(var(--primary)/0.2)'
                    }}
                >
                    <EditableContent id={`hero.flip.word.${currentIndex}`} />
                </motion.span>
            </AnimatePresence>

            {/* Underline animation */}
            <motion.div
                key={`underline-${currentIndex}`}
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className={cn(
                    "absolute -bottom-2 h-1 bg-gradient-to-r from-primary via-primary/80 to-primary rounded-full",
                    "start-0 ltr:origin-left rtl:origin-right",
                    "w-full will-change-transform transform-gpu"
                )}
            />
        </span>
    );
};
